import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Typography, Link, Select, MenuItem, IconButton, LinearProgress, FormHelperText, Skeleton} from '@mui/material'
import {Close, UploadFile} from '@mui/icons-material'
import {useDropzone} from 'react-dropzone'
import {TextSkeleton} from '../Skeletons'

const MAX_SIZE = 3 * 1024 * 1024

// With simulateProgress (used where there is no upload endpoint yet), picked
// files only live in the form and the progress is faked so the list looks
// like the design.
const PROGRESS_STEP = 20
const PROGRESS_INTERVAL = 200

let nextId = 0

export const formatFileSize = bytes => {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}kb`
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

const rejectionMessage = rejection => {
    const code = rejection.errors[0]?.code
    if (code === 'file-too-large') return `${rejection.file.name} is larger than 3MB`
    if (code === 'file-invalid-type') return `${rejection.file.name} is not a PDF`
    return `${rejection.file.name} could not be added`
}

const entryName = entry => entry.name ?? entry.file?.name
const entrySize = entry => entry.size ?? entry.file?.size ?? 0

const statusLabel = (entry, simulateProgress) => {
    if (entry.progress >= 100) return 'Complete'
    if (entry.uploading || simulateProgress) return 'Loading'
    return 'Ready to upload'
}

// Controlled list of documents. value is [{id, category, progress, ...}]:
// a picked file carries `file`; a saved one carries `name`, `size` and
// `url` instead. `uploading`, `error` and `categoryError` are set by the
// owner while it saves. loading shows a placeholder file while the owner fetches the saved documents.
export default ({label, categories = [], value = [], onChange, simulateProgress = true, disabled: disabledProp = false, loading = false}) => {
    const disabled = disabledProp || loading
    const [errors, setErrors] = useState([])
    const filesRef = useRef(value)
    filesRef.current = value
    // Keep the ref ahead of the re-render so back-to-back changes build on each other.
    const emit = next => {
        filesRef.current = next
        onChange(next)
    }

    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        accept: {'application/pdf': ['.pdf']},
        noClick: true,
        multiple: true,
        maxSize: MAX_SIZE,
        disabled,
        onDrop: (acceptedFiles, fileRejections) => {
            setErrors(fileRejections.map(rejectionMessage))
            if (acceptedFiles.length === 0) return
            const added = acceptedFiles.map(file => ({id: `doc-${++nextId}`, file, category: '', progress: 0}))
            emit([...filesRef.current, ...added])
        },
    })

    const simulating = simulateProgress && value.some(entry => entry.progress < 100)
    useEffect(() => {
        if (!simulating) return
        const timer = setInterval(() => {
            emit(filesRef.current.map(entry => (entry.progress < 100 ? {...entry, progress: Math.min(100, entry.progress + PROGRESS_STEP)} : entry)))
        }, PROGRESS_INTERVAL)
        return () => clearInterval(timer)
    }, [simulating])

    const updateEntry = (id, changes) => emit(filesRef.current.map(entry => (entry.id === id ? {...entry, ...changes} : entry)))
    const removeEntry = id => emit(filesRef.current.filter(entry => entry.id !== id))

    return (
        <div className="document-upload">
            <Typography variant="inputLabel" color="text.secondary">
                {label}
            </Typography>
            <div className="row">
                <div className="dropzone-wrapper">
                    <div {...getRootProps({className: `dropzone${isDragActive ? ' active' : ''}${disabled ? ' disabled' : ''}`})}>
                        <input {...getInputProps()} aria-label={label} />
                        <UploadFile style={{fontSize: 26}} color="primary" />
                        <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                            <Link
                                component="button"
                                type="button"
                                underline="always"
                                onClick={open}
                                disabled={disabled}
                                style={{verticalAlign: 'baseline'}}>
                                Click to upload
                            </Link>{' '}
                            or drag and drop
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            PDF (max. 3MB)
                        </Typography>
                    </div>
                    {errors.map(error => (
                        <FormHelperText key={error} error>
                            {error}
                        </FormHelperText>
                    ))}
                </div>
                {loading && (
                    <div className="file-list">
                        <div className="file-item">
                            <Skeleton variant="rounded" width={24} height={24} className="file-icon" />
                            <div className="file-body">
                                <TextSkeleton width={110} fontSize={12} />
                                <Skeleton variant="rounded" height={40} sx={{borderRadius: 4}} />
                                <TextSkeleton width="60%" fontSize={16} />
                                <TextSkeleton width={100} fontSize={12} />
                                <Skeleton variant="rounded" height={4} sx={{borderRadius: 0.5}} />
                            </div>
                            <Skeleton variant="circular" width={20} height={20} sx={{m: 0.625}} />
                        </div>
                    </div>
                )}
                {!loading && value.length > 0 && (
                    <div className="file-list">
                        {value.map(entry => (
                            <div className="file-item" key={entry.id}>
                                <UploadFile color="primary" className="file-icon" />
                                <div className="file-body">
                                    <Typography variant="caption" color="text.primary">
                                        Document Category
                                    </Typography>
                                    <Select
                                        size="small"
                                        fullWidth
                                        displayEmpty
                                        disabled={disabled}
                                        error={!!entry.categoryError}
                                        value={entry.category}
                                        onChange={e => updateEntry(entry.id, {category: e.target.value, categoryError: false})}
                                        inputProps={{'aria-label': `Document category for ${entryName(entry)}`}}
                                        // Open upward, so the menu doesn't cover the files listed below.
                                        MenuProps={{
                                            PaperProps: {style: {maxHeight: 300}},
                                            anchorOrigin: {vertical: 'top', horizontal: 'left'},
                                            transformOrigin: {vertical: 'bottom', horizontal: 'left'},
                                        }}
                                        renderValue={selected => {
                                            if (selected === '') {
                                                return <span style={{color: '#aaa'}}>Select document category</span>
                                            }
                                            return categories.find(category => category.value === selected)?.label ?? selected
                                        }}>
                                        {categories.map(category => (
                                            <MenuItem key={category.value} value={category.value}>
                                                {category.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {entry.categoryError && <FormHelperText error>Document category is required</FormHelperText>}
                                    {entry.url ? (
                                        <Link href={entry.url} target="_blank" rel="noopener noreferrer" variant="body1" className="file-name">
                                            {entryName(entry)}
                                        </Link>
                                    ) : (
                                        <Typography variant="body1" color="text.primary" className="file-name">
                                            {entryName(entry)}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {formatFileSize(entrySize(entry))} • {statusLabel(entry, simulateProgress)}
                                    </Typography>
                                    <LinearProgress variant="determinate" value={entry.progress} className="file-progress" />
                                    {entry.error && <FormHelperText error>{entry.error}</FormHelperText>}
                                </div>
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${entryName(entry)}`}
                                    disabled={disabled}
                                    onClick={() => removeEntry(entry.id)}>
                                    <Close />
                                </IconButton>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
